// src/services/invoiceMoney.js
//
// Money on upgrade invoices (invitation tier, photo uploads, album storage).
// Most are in rupees for buyers in India; buyers abroad pay in their own
// currency (see shared/upgradePricing.js), and their invoice is an export of
// services with IGST charged, as there is no LUT.

const WORDS = {
  INR: ['Rupee', 'Rupees', 'Paisa', 'Paise'],
  USD: ['US Dollar', 'US Dollars', 'Cent', 'Cents'],
  EUR: ['Euro', 'Euros', 'Cent', 'Cents'],
  GBP: ['Pound Sterling', 'Pounds Sterling', 'Penny', 'Pence'],
  CAD: ['Canadian Dollar', 'Canadian Dollars', 'Cent', 'Cents'],
  AUD: ['Australian Dollar', 'Australian Dollars', 'Cent', 'Cents'],
  AED: ['UAE Dirham', 'UAE Dirhams', 'Fils', 'Fils'],
  SGD: ['Singapore Dollar', 'Singapore Dollars', 'Cent', 'Cents'],
};

const UNITS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const belowThousand = (n) => {
  const parts = [];
  if (n >= 100) {
    parts.push(`${UNITS[Math.floor(n / 100)]} Hundred`);
    n %= 100;
    if (n) parts.push('and');
  }
  if (n >= 20) {
    parts.push(TENS[Math.floor(n / 10)] + (n % 10 ? ` ${UNITS[n % 10]}` : ''));
  } else if (n > 0) {
    parts.push(UNITS[n]);
  }
  return parts.join(' ');
};

/** Lakh and crore for rupees, thousand and million for everything else. */
const integerToWords = (num, indian) => {
  let n = Math.floor(num);
  if (n === 0) return 'Zero';
  const scales = indian
    ? [[10000000, 'Crore'], [100000, 'Lakh'], [1000, 'Thousand']]
    : [[1000000000, 'Billion'], [1000000, 'Million'], [1000, 'Thousand']];
  const parts = [];
  for (const [size, name] of scales) {
    if (n >= size) {
      parts.push(`${integerToWords(Math.floor(n / size), indian)} ${name}`);
      n %= size;
    }
  }
  if (n) parts.push(belowThousand(n));
  return parts.join(' ');
};

export const invoiceCurrency = (invoice) => String(invoice?.currency || 'INR').toUpperCase();

/** "Rs. 116.82" in rupees, "USD 4.99" otherwise: the ISO code is unambiguous on paper. */
export const moneyOnInvoice = (amount, currency = 'INR') => {
  const n = (Number(amount) || 0).toFixed(2);
  return currency === 'INR' ? `Rs. ${n}` : `${currency} ${n}`;
};

/** E.g. 116.82 INR => "One Hundred and Sixteen Rupees and Eighty Two Paise". */
export const amountInWords = (amount, currency = 'INR') => {
  const [one, many, minorOne, minorMany] = WORDS[currency] || [currency, currency, 'Cent', 'Cents'];
  const value = Math.round((Number(amount) || 0) * 100);
  const major = Math.floor(value / 100);
  const minor = value % 100;
  const indian = currency === 'INR';

  const parts = [];
  if (major > 0) parts.push(`${integerToWords(major, indian)} ${major === 1 ? one : many}`);
  if (minor > 0) parts.push(`${integerToWords(minor, indian)} ${minor === 1 ? minorOne : minorMany}`);
  return parts.join(' and ') || `Zero ${many}`;
};

/** A buyer outside India: an export of services. */
export const isExportInvoice = (invoice) => {
  const country = String(invoice?.customer_country || 'IN').toUpperCase();
  return country !== 'IN';
};

const countryName = (code) => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) || code;
  } catch {
    return code;
  }
};

/** "Maharashtra (27)" in India, "Outside India (United States)" abroad. */
export const placeOfSupply = (invoice) => {
  if (isExportInvoice(invoice)) {
    return `Outside India (${countryName(String(invoice.customer_country).toUpperCase())})`;
  }
  const { customer_state: state, customer_state_code: code } = invoice || {};
  if (!state) return 'N/A';
  return code ? `${state} (${code})` : state;
};

/** The supply note under the totals. */
export const supplyNote = (invoice) => {
  if (isExportInvoice(invoice)) {
    return `Export of services to ${countryName(String(invoice.customer_country).toUpperCase())} with payment of IGST. Amounts in ${invoiceCurrency(invoice)}.`;
  }
  return invoice?.gst_type === 'intrastate'
    ? 'Intrastate supply - CGST and SGST applicable.'
    : 'Interstate supply - IGST applicable.';
};
